import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { uploadToAzure } from '../direct_uploads/tiptap_direct_upload.js';
import '@/components/tiptap-node/image-upload-node/image-upload-node.scss';
import { carouselSlideFromResource } from '../lib/carousel_slide_from_resource.js';
import {
  IMAGE_ASPECT_RATIO_ERROR,
  IMAGE_ASPECT_RATIO_HINT,
  validateImageAspectRatio,
} from '@/lib/image-aspect-ratio-validation';

function resourceImageUrl(resource) {
  if (!resource || resource.file_type !== 'IMAGE') return null;
  if (resource.downloadable_file_attachment_url) {
    return resource.downloadable_file_attachment_url;
  }
  if (resource.display_image_file_url) {
    return resource.display_image_file_url;
  }
  return null;
}

function matchSlideToResource(slide, resources) {
  if (!slide || !Array.isArray(resources) || resources.length === 0) return null;

  const slideId = slide.resourceId != null ? String(slide.resourceId) : null;
  const slideSrc = typeof slide.src === 'string' ? slide.src : null;

  if (slideId) {
    const byId = resources.find((resource) => String(resource.id) === slideId) || null;
    if (byId) return byId;
  }

  if (slideSrc) {
    return (
      resources.find((resource) => {
        const url = resource.downloadable_file_attachment_url || resource.display_image_file_url || null;
        return url && url === slideSrc;
      }) || null
    );
  }

  return null;
}

function matchSlidesToResources(slides, resources) {
  if (!Array.isArray(slides) || slides.length === 0) return [];

  const matched = [];
  const seen = new Set();

  for (const slide of slides) {
    const resource = matchSlideToResource(slide, resources);
    if (resource && resource.id != null && !seen.has(String(resource.id))) {
      seen.add(String(resource.id));
      matched.push(resource);
    }
  }

  return matched;
}

function sortResourcesWithPickedOnTop(resources, picked) {
  if (!picked?.length || !resources?.length) return resources;

  const pickedOrder = new Map();
  picked.forEach((r, i) => {
    if (r?.id != null) pickedOrder.set(String(r.id), i);
  });

  const top = [];
  const rest = [];
  for (const r of resources) {
    if (r?.id != null && pickedOrder.has(String(r.id))) top.push(r);
    else rest.push(r);
  }
  top.sort((a, b) => pickedOrder.get(String(a.id)) - pickedOrder.get(String(b.id)));
  return [...top, ...rest];
}

/**
 * ResourceModal - Modal for selecting existing resources or uploading new ones
 *
 * @param {string} fileType - 'image', 'video', 'download', 'link', or 'carousel'
 * @param {Function} onCarouselInsert - When fileType is carousel: (slides) => void
 * @param {Array}    existingCarouselSlides - When opening to EDIT an existing
 *   carousel: pass the carousel's current slides (each `{src, resourceId?}`).
 *   When set, the modal switches to edit-carousel mode: it pre-selects the
 *   matching resources, sorts them to the top of the grid, and surfaces an
 *   "Update carousel" action wired to `onCarouselUpdate` instead of insert.
 * @param {Array}    lockedCarouselSlides - When replacing a single carousel
 *   slide, pass the other slides in that carousel so their resources can be
 *   shown as disabled in the grid.
 * @param {Object}   replaceCarouselSlide - The slide currently being replaced;
 *   its matching resource is preselected in the grid.
 * @param {Function} onCarouselUpdate - (slides) => void — replaces the current
 *   carousel's slides when in edit-carousel mode. Caller is responsible for
 *   identifying which carousel to update (typically via a captured nodePos).
 */
export const ResourceModal = ({
  isOpen,
  fileType,
  onClose,
  onSelect,
  onCarouselInsert,
  onCarouselUpdate,
  existingCarouselSlides,
  lockedCarouselSlides = [],
  replaceCarouselSlide = null,
  editor,
  initialLinkUrl = '',
  initialFiles = null,
}) => {
  const [resources, setResources] = useState([]);
  const [carouselPicked, setCarouselPicked] = useState([]);
  const [mediaPicked, setMediaPicked] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [searchQuery, setSearchQuery] = useState('');
  const [linkUrl, setLinkUrl] = useState('');
  const [linkTitle, setLinkTitle] = useState('');
  const [creatingLink, setCreatingLink] = useState(false);
  const [fileItems, setFileItems] = useState([]);
  const [uploadError, setUploadError] = useState(null);
  const fileInputRef = useRef(null);
  // Edit-carousel grid order: sort picked items to top once per asset list, not on every toggle.
  const gridOrderCacheRef = useRef(null);

  // Use uploadToAzure which provides both URL and signed_id needed for resource creation
  // This matches the same upload mechanism used by the paid extensions

  // Set initial link URL when modal opens with initialLinkUrl prop
  useEffect(() => {
    if (isOpen && initialLinkUrl && fileType === 'link') {
      setLinkUrl(initialLinkUrl);
    } else if (isOpen && !initialLinkUrl) {
      // Reset when modal opens without initial URL
      setLinkUrl('');
      setLinkTitle('');
    }
  }, [isOpen, initialLinkUrl, fileType]);

  // Reset search query when modal opens or fileType changes
  useEffect(() => {
    if (isOpen) {
      setSearchQuery('');
      setUploadError(null);
    }
  }, [isOpen, fileType]);

  useEffect(() => {
    if (!isOpen) gridOrderCacheRef.current = null;
  }, [isOpen]);

  // Handle paste events when modal is open
  useEffect(() => {
    if (!isOpen || uploading) return;

    const handlePaste = async (e) => {
      // Only handle paste if modal is open and not uploading
      if (!isOpen || uploading) return;

      // Check if clipboard contains files
      const clipboardData = e.clipboardData || e.originalEvent?.clipboardData;
      if (!clipboardData) return;

      const items = clipboardData.items;
      if (!items) return;

      // Find files in clipboard
      const files = [];
      for (let i = 0; i < items.length; i++) {
        const item = items[i];
        if (item.kind === 'file') {
          const file = item.getAsFile();
          if (file) {
            files.push(file);
          }
        }
      }

      // If files found, upload them
      if (files.length > 0) {
        e.preventDefault();
        e.stopPropagation();
        await handleFileUpload(files);
      }
    };

    // Add paste event listener to document
    document.addEventListener('paste', handlePaste);

    return () => {
      document.removeEventListener('paste', handlePaste);
    };
  }, [isOpen, uploading, handleFileUpload]);

  // Fetch resources when modal opens
  useEffect(() => {
    if (isOpen) {
      fetchResources();
    }
  }, [isOpen, fileType]);

  // Edit-carousel mode is signalled by passing `existingCarouselSlides` along
  // with `fileType === 'image'` (the modal we use for both single-media insert
  // and 2+-pick carousel build). When the array is non-empty we treat this as
  // an edit session: skip the normal "clear picks on open" behaviour and let
  // the pre-population effect below seed `mediaPicked` instead.
  const isEditingCarousel =
    Array.isArray(existingCarouselSlides) && existingCarouselSlides.length > 0;
  const isReplacingCarouselSlide = !!replaceCarouselSlide;
  const showCarouselOrderPanel =
    fileType === 'image' && !isReplacingCarouselSlide && mediaPicked.length > 1;
  const carouselSeedSlides = useMemo(
    () => (isReplacingCarouselSlide ? [replaceCarouselSlide].filter(Boolean) : existingCarouselSlides),
    [isReplacingCarouselSlide, replaceCarouselSlide, existingCarouselSlides],
  );

  useEffect(() => {
    if (isOpen && fileType === 'carousel') {
      setCarouselPicked([]);
    }
  }, [isOpen, fileType]);

  useEffect(() => {
    if (isOpen && fileType === 'image' && !isEditingCarousel && !isReplacingCarouselSlide) {
      setMediaPicked([]);
    }
  }, [isOpen, fileType, isEditingCarousel, isReplacingCarouselSlide]);

  // When opening in edit-carousel or single-slide replace mode, match each
  // slide back to a resource (by resourceId first, falling back to URL match
  // for legacy carousels that pre-date the resourceId attribute) and seed
  // `mediaPicked` so the existing members render with the same red selection
  // ring as a freshly-clicked tile.
  useEffect(() => {
    if (!isOpen || (!isEditingCarousel && !isReplacingCarouselSlide)) return;
    if (!resources || resources.length === 0) return;

    setMediaPicked(matchSlidesToResources(carouselSeedSlides, resources));
    // Only re-seed when the modal first opens or the slides/resources change.
  }, [isOpen, isEditingCarousel, isReplacingCarouselSlide, carouselSeedSlides, resources]);

  const disabledResourceIds = (() => {
    if (!isReplacingCarouselSlide) return new Set();
    const lockedResources = matchSlidesToResources(lockedCarouselSlides, resources);
    return new Set(lockedResources.map((resource) => String(resource.id)));
  })();

  const movePickedMedia = useCallback((fromIndex, toIndex) => {
    setMediaPicked((prev) => {
      if (
        !Array.isArray(prev) ||
        fromIndex < 0 ||
        toIndex < 0 ||
        fromIndex >= prev.length ||
        toIndex >= prev.length ||
        fromIndex === toIndex
      ) {
        return prev;
      }

      const next = [...prev];
      const [moved] = next.splice(fromIndex, 1);
      next.splice(toIndex, 0, moved);
      return next;
    });
  }, []);

  // Auto-upload initial files when modal opens
  useEffect(() => {
    if (isOpen && initialFiles && initialFiles.length > 0 && !uploading) {
      handleFileUpload(initialFiles);
    }
  }, [isOpen, initialFiles, uploading, handleFileUpload]);

  const fetchResources = async () => {
    setLoading(true);
    try {
      // For image type, fetch both images and videos (both use downloadable_file_attachment)
      // For other types, fetch specific type
      let resources = [];
      
      if (fileType === 'image' || fileType === 'video' || fileType === 'carousel') {
        // Fetch all media resources (both images and videos) in one call, ordered by latest first
        const response = await fetch(`/admin/resources.json?file_type=image&per_page=100`, {
          headers: {
            'Accept': 'application/json',
            'X-CSRF-Token': document.querySelector('meta[name=csrf-token]')?.getAttribute('content') || ''
          }
        });

        if (!response.ok) {
          throw new Error('Failed to fetch resources');
        }

        const data = await response.json();
        resources = data.resources || [];
        
        // Sort by created_at descending (latest first) to ensure proper ordering
        resources.sort((a, b) => {
          const dateA = new Date(a.created_at);
          const dateB = new Date(b.created_at);
          return dateB - dateA; // Descending order (newest first)
        });
      } else if (fileType === 'download') {
        // Fetch downloads from downloadable_file_attachment
        const response = await fetch(`/admin/resources.json?file_type=download&per_page=50`, {
          headers: {
            'Accept': 'application/json',
            'X-CSRF-Token': document.querySelector('meta[name=csrf-token]')?.getAttribute('content') || ''
          }
        });

        if (!response.ok) {
          throw new Error('Failed to fetch resources');
        }

        const data = await response.json();
        resources = data.resources || [];
        
        // Sort by created_at descending (latest first) to ensure proper ordering
        resources.sort((a, b) => {
          const dateA = new Date(a.created_at);
          const dateB = new Date(b.created_at);
          return dateB - dateA; // Descending order (newest first)
        });
      } else if (fileType === 'link') {
        // Fetch links (resources with clickable_link)
        const response = await fetch(`/admin/resources.json?file_type=link&per_page=50`, {
          headers: {
            'Accept': 'application/json',
            'X-CSRF-Token': document.querySelector('meta[name=csrf-token]')?.getAttribute('content') || ''
          }
        });

        if (!response.ok) {
          throw new Error('Failed to fetch resources');
        }

        const data = await response.json();
        resources = data.resources || [];
        
        // Sort by created_at descending (latest first) to ensure proper ordering
        resources.sort((a, b) => {
          const dateA = new Date(a.created_at);
          const dateB = new Date(b.created_at);
          return dateB - dateA; // Descending order (newest first)
        });
      } else if (fileType === 'audio') {
        // Fetch audio resources (media with audio content_type, not in recently added)
        const response = await fetch(`/admin/resources.json?file_type=audio&per_page=50`, {
          headers: {
            'Accept': 'application/json',
            'X-CSRF-Token': document.querySelector('meta[name=csrf-token]')?.getAttribute('content') || ''
          }
        });

        if (!response.ok) {
          throw new Error('Failed to fetch resources');
        }

        const data = await response.json();
        resources = data.resources || [];
        
        resources.sort((a, b) => {
          const dateA = new Date(a.created_at);
          const dateB = new Date(b.created_at);
          return dateB - dateA;
        });
      }
      
      setResources(resources);
    } catch (error) {
      console.error('Error fetching resources:', error);
      setResources([]);
    } finally {
      setLoading(false);
    }
  };

  const validateImageFilesAspectRatio = async (imageFiles) => {
    for (const file of imageFiles) {
      if ((file.type || '').startsWith('image/')) {
        await validateImageAspectRatio(file);
      }
    }
  };

  // Handle file upload using paid extension upload function
  const handleFileUpload = useCallback(async (files) => {
    if (!files || files.length === 0) return;

    setUploadError(null);

    // Image/Video modal: never auto-insert. Upload adds assets to the left grid
    // and pre-selects them so user can insert 1 (single media) or 2+ (carousel).
    if (fileType === 'image') {
      const mediaFiles = Array.from(files).filter((f) => {
        const t = (f.type || '').toLowerCase();
        return t.startsWith('image/') || t.startsWith('video/');
      });
      if (!mediaFiles.length) {
        alert('Please select an image or video file');
        return;
      }

      try {
        await validateImageFilesAspectRatio(mediaFiles);
      } catch (error) {
        setUploadError(error?.message || IMAGE_ASPECT_RATIO_ERROR);
        return;
      }

      setUploading(true);
      setUploadProgress(0);
      const initialItems = mediaFiles.map((file) => ({
        id: crypto.randomUUID(),
        file,
        progress: 0,
        status: 'uploading',
      }));
      setFileItems(initialItems);
      const created = [];

      try {
        for (let i = 0; i < mediaFiles.length; i += 1) {
          const file = mediaFiles[i];
          const fileId = initialItems[i]?.id;
          const uploadResult = await uploadToAzure(file, {
            onProgress: (progress) => {
              // Overall progress for the top bar (kept for compatibility)
              setUploadProgress(Math.round(((i + progress / 100) / mediaFiles.length) * 100));
              // Per-file row progress
              if (fileId) {
                setFileItems((prev) =>
                  prev.map((item) =>
                    item.id === fileId ? { ...item, progress, status: 'uploading' } : item,
                  ),
                );
              }
            },
          });

          if (!uploadResult.url || !uploadResult.signed_id) {
            throw new Error('Upload failed: Missing URL or signed_id');
          }

          if (fileId) {
            setFileItems((prev) =>
              prev.map((item) =>
                item.id === fileId ? { ...item, progress: 100, status: 'success', url: uploadResult.url } : item,
              ),
            );
          }

          const resourceResult = await createResourceFromUpload(file, uploadResult);
          created.push(resourceResult);
        }

        await fetchResources();

        // Preselect newly uploaded items
        setMediaPicked((prev) => {
          const next = [...prev];
          for (const r of created) {
            if (!r || !r.id) continue;
            if (!next.find((x) => x?.id === r.id)) next.push(r);
          }
          return next;
        });
      } catch (error) {
        console.error('Error uploading media:', error);
        alert('Upload failed: ' + (error.message || error));
      } finally {
        setUploading(false);
        setUploadProgress(0);
        setFileItems([]);
      }

      return;
    }

    if (fileType === 'carousel' && files.length >= 2 && onCarouselInsert) {
      const imageFiles = Array.from(files).filter((f) => f.type.startsWith('image/'));
      if (imageFiles.length < 2) {
        alert('Please select at least two image files for a carousel');
        return;
      }

      try {
        await validateImageFilesAspectRatio(imageFiles);
      } catch (error) {
        setUploadError(error?.message || IMAGE_ASPECT_RATIO_ERROR);
        return;
      }

      setUploading(true);
      setUploadProgress(0);
      setFileItems([]);
      const slides = [];
      try {
        for (let i = 0; i < imageFiles.length; i += 1) {
          const file = imageFiles[i];
          const uploadResult = await uploadToAzure(file, {
            onProgress: (progress) => {
              setUploadProgress(Math.round(((i + (progress / 100)) / imageFiles.length) * 100));
            },
          });
          if (!uploadResult.url || !uploadResult.signed_id) {
            throw new Error('Upload failed: Missing URL or signed_id');
          }
          const resourceResult = await createResourceFromUpload(file, uploadResult);
          const url =
            resourceResult.downloadable_file_attachment_url ||
            resourceResult.display_image_file_url ||
            uploadResult.url;
          if (url) {
            slides.push({
              src: url,
              caption: '',
              alt: resourceResult.title || file.name || '',
            });
          }
        }
        await fetchResources();
        if (slides.length >= 2) {
          onCarouselInsert(slides);
          onClose();
        }
      } catch (error) {
        console.error('Error uploading carousel images:', error);
        alert('Upload failed: ' + (error.message || error));
      } finally {
        setUploading(false);
        setUploadProgress(0);
        setFileItems([]);
      }
      return;
    }

    const file = files[0]; // ResourceModal only handles one file at a time

    // Validate file type
    const mimeType = file.type.toLowerCase();

    if (fileType === 'image') {
      // Allow both images and videos for image type
      if (!mimeType.startsWith('image/') && !mimeType.startsWith('video/')) {
        alert('Please select an image or video file');
        return;
      }
    } else if (fileType === 'video' && !mimeType.startsWith('video/')) {
      alert('Please select a video file');
      return;
    } else if (fileType === 'audio' && !mimeType.startsWith('audio/')) {
      alert('Please select an audio file');
      return;
    } else if (fileType === 'carousel') {
      alert('Select at least two images at once from your computer, or pick multiple image assets from the grid.');
      return;
    }

    setUploading(true);
    setUploadProgress(0);

    // Create file item for tracking (mimics paid extension fileItems)
    const fileId = crypto.randomUUID();
    const newFileItem = {
      id: fileId,
      file,
      progress: 0,
      status: 'uploading',
    };
    setFileItems([newFileItem]);

    try {
      // Step 1: Upload file using uploadToAzure (same as paid extensions use)
      // This provides both URL and signed_id needed for resource creation
      const uploadResult = await uploadToAzure(file, {
        onProgress: (progress) => {
          setUploadProgress(progress);
          setFileItems(prev => prev.map(item => 
            item.id === fileId ? { ...item, progress, status: 'uploading' } : item
          ));
        }
      });

      if (!uploadResult.url || !uploadResult.signed_id) {
        throw new Error('Upload failed: Missing URL or signed_id');
      }

      // Update file item to success status
      setFileItems(prev => prev.map(item => 
        item.id === fileId ? { ...item, progress: 100, status: 'success', url: uploadResult.url } : item
      ));

      // Step 2: Create resource from uploaded file
      const resourceResult = await createResourceFromUpload(file, uploadResult);

      // Step 3: Refresh resources list
      await fetchResources();

      // Step 4: Select the newly created resource and close modal
      if (onSelect) {
        onSelect(resourceResult, fileType);
      }

      // Reset upload state
      setUploading(false);
      setUploadProgress(0);
      setFileItems([]);
      
      // Close modal after successful upload and resource creation
      onClose();
    } catch (error) {
      console.error('Error uploading file:', error);
      alert('Upload failed: ' + (error.message || error));
      setUploading(false);
      setUploadProgress(0);
      setFileItems([]);
    }
  }, [fileType, onSelect, onClose, onCarouselInsert]);

  const handleFileSelect = async (event) => {
    const files = Array.from(event.target.files || []);
    if (!files.length) return;
    if (fileType === 'carousel' || fileType === 'image') {
      await handleFileUpload(files);
    } else {
      await handleFileUpload([files[0]]);
    }
    event.target.value = '';
  };

  const createResourceFromUpload = async (file, uploadResult) => {
    const csrfToken = document.querySelector('meta[name=csrf-token]')?.getAttribute('content') || '';

      // All file uploads use downloadable_file_attachment
      // Set resource_type based on fileType (context, not actual file type)
      // media = image/video/audio extension (not in recently added), download = download extension, link = link extension
      let resourceType = 'download'; // Default to download
      if (fileType === 'image' || fileType === 'video' || fileType === 'audio' || fileType === 'carousel') {
        resourceType = 'media';
      } else if (fileType === 'download') {
        resourceType = 'download';
      } else if (fileType === 'link') {
        resourceType = 'link';
      }
      
      const resourceData = {
        resource: {
          title: file.name.replace(/\.[^/.]+$/, ''), // Remove extension
          description: '', // Leave description empty
          resource_category_id: null, // Resources created from posts are uncategorized
          resource_type: resourceType,
          downloadable_file_attachment_signed_id: uploadResult.signed_id
        }
      };

    const response = await fetch('/admin/resources.json', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
        'X-CSRF-Token': csrfToken
      },
      body: JSON.stringify(resourceData)
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.error || 'Failed to create resource');
    }

    return await response.json();
  };

  // Helper function to highlight matching text
  const highlightText = (text, query) => {
    if (!text || !query) return text;
    
    const textStr = String(text);
    const queryStr = String(query);
    const regex = new RegExp(`(${queryStr.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'gi');
    const parts = textStr.split(regex);
    
    return parts.map((part, index) => {
      if (part.toLowerCase() === queryStr.toLowerCase()) {
        return <span key={index} style={{ backgroundColor: '#ffff00', color: '#000' }}>{part}</span>;
      }
      return part;
    });
  };

  const getResourceUrl = (resource) => {
    if (fileType === 'carousel') {
      return resourceImageUrl(resource);
    }
    // For images, videos, audio, and downloads, use downloadable_file_attachment_url
    if ((fileType === 'image' || fileType === 'video' || fileType === 'audio' || fileType === 'download') && resource.downloadable_file_attachment_url) {
      return resource.downloadable_file_attachment_url;
    }
    // For links, use clickable_link
    if (fileType === 'link' && resource.clickable_link) {
      return resource.clickable_link;
    }
    return null;
  };

  const getResourcePreview = (resource) => {
    const url = getResourceUrl(resource);
    if (!url) return null;

    // Check the actual resource file_type to determine how to render
    // For images and videos, check if they have display_image_file_url
    // For downloads, check downloadable_file_attachment_url
    // For links, show link icon
    if (fileType === 'image' || fileType === 'video' || fileType === 'carousel') {
      // Top section: show filename when available; media only otherwise
      const filename = resource.downloadable_file_filename || null;
      const posterUrl = resource.display_image_file_url || null;
      const media = resource.file_type === 'VIDEO' && fileType !== 'carousel' ? (
        <video src={url} poster={posterUrl || undefined} controls={false} style={{ width: '100%', height: 'auto', maxHeight: '150px' }} muted playsInline />
      ) : (
        <img src={url} alt={resource.title} style={{ width: '100%', height: 'auto', maxHeight: '150px', objectFit: 'cover' }} />
      );
      return (
        <div>
          {media}
          {filename && (
            <div style={{ marginTop: '4px', fontSize: '12px', color: '#666', wordWrap: 'break-word' }}>
              {searchQuery ? highlightText(filename, searchQuery) : filename}
            </div>
          )}
        </div>
      );
    } else if (fileType === 'audio') {
      // Top section: show filename (bottom section shows resource title)
      const filename = resource.downloadable_file_filename || resource.title || 'Audio';
      return (
        <div style={{ padding: '12px', background: '#f5f5f5', borderRadius: '4px' }}>
          <audio src={url} controls={false} style={{ width: '100%', maxHeight: '36px' }} />
          <div style={{ marginTop: '6px', fontSize: '13px', color: '#000', wordWrap: 'break-word' }}>
            {searchQuery ? highlightText(filename, searchQuery) : filename}
          </div>
        </div>
      );
    } else if (fileType === 'link') {
      // Links - show clickable link with highlighting
      const displayText = resource.clickable_link || resource.title;
      return (
        <div className="resource-preview-container" style={{ padding: '20px', textAlign: 'center', wordWrap: 'break-word', overflowWrap: 'break-word' }}>
          🔗 {searchQuery ? highlightText(displayText, searchQuery) : displayText}
        </div>
      );
    } else {
      // Downloads: top section shows filename (bottom section shows resource title)
      const filename = resource.downloadable_file_filename || resource.title;
      return (
        <div className="resource-preview-container" style={{ padding: '20px', textAlign: 'center', wordWrap: 'break-word', overflowWrap: 'break-word' }}>
          📄 {searchQuery ? highlightText(filename, searchQuery) : filename}
        </div>
      );
    }
  };

  const getPickedMediaThumb = (resource) => {
    if (!resource) return null;

    const mediaUrl =
      resource.downloadable_file_attachment_url || resource.display_image_file_url || null;
    const posterUrl = resource.display_image_file_url || null;

    if (!mediaUrl) {
      return <div className="resource-picked-order__thumb-fallback">No preview</div>;
    }

    if (resource.file_type === 'VIDEO' && posterUrl) {
      return <img src={posterUrl} alt={resource.title || 'Video preview'} />;
    }

    if (resource.file_type === 'VIDEO') {
      return <video src={mediaUrl} muted playsInline aria-hidden="true" />;
    }

    return <img src={mediaUrl} alt={resource.title || 'Image preview'} />;
  };

  const createLinkResource = async () => {
    if (!linkUrl || !linkUrl.trim()) {
      alert('Please enter a URL');
      return;
    }

    setCreatingLink(true);
    const csrfToken = document.querySelector('meta[name=csrf-token]')?.getAttribute('content') || '';

    try {
      // Format URL
      let formattedUrl = linkUrl.trim();
      if (!formattedUrl.startsWith('http://') && !formattedUrl.startsWith('https://')) {
        formattedUrl = `https://${formattedUrl}`;
      }

      const resourceData = {
        resource: {
          title: linkTitle.trim() || formattedUrl,
          description: '', // Leave description empty
          clickable_link: formattedUrl,
          resource_category_id: null, // Resources created from posts are uncategorized
          resource_type: 'link' // Links have resource_type = link
        }
      };

      const response = await fetch('/admin/resources.json', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'X-CSRF-Token': csrfToken
        },
        body: JSON.stringify(resourceData)
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || 'Failed to create resource');
      }

      const resourceResult = await response.json();

      // Refresh resources list
      await fetchResources();

      // Select the newly created resource and close modal
      if (onSelect) {
        onSelect(resourceResult, fileType);
      }

      // Reset form
      setLinkUrl('');
      setLinkTitle('');
      setCreatingLink(false);
      
      // Close modal
      onClose();
    } catch (error) {
      console.error('Error creating link resource:', error);
      alert('Failed to create link: ' + (error.message || error));
      setCreatingLink(false);
    }
  };

  const filteredResources = resources.filter(resource => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    
    // Always search in title and description
    const matchesTitle = resource.title?.toLowerCase().includes(query);
    const matchesDescription = resource.description?.toLowerCase().includes(query);
    
    // For links, also search in clickable_link
    const matchesLink = fileType === 'link' && resource.clickable_link?.toLowerCase().includes(query);
    
    // For downloads, also search in downloadable_file_filename
    const matchesFilename = fileType === 'download' && resource.downloadable_file_filename?.toLowerCase().includes(query);
    
    return matchesTitle || matchesDescription || matchesLink || matchesFilename;
  });

  const baseGridResources =
    fileType === 'carousel'
      ? filteredResources.filter((r) => resourceImageUrl(r))
      : fileType === 'image'
        ? filteredResources.filter((r) => r?.file_type === 'IMAGE' || r?.file_type === 'VIDEO')
        : filteredResources;

  // In edit-carousel mode, lift the carousel's current members to the top of
  // the grid once when the asset list is first shown (not on every pick toggle,
  // which reordered the DOM and reset scroll). Selection rings still update live.
  const gridResources = useMemo(() => {
    if (!isEditingCarousel && !isReplacingCarouselSlide) return baseGridResources;

    const baseKey = baseGridResources.map((r) => r.id).join(',');
    const cached = gridOrderCacheRef.current;
    if (cached?.baseKey === baseKey) return cached.resources;

    if (!mediaPicked.length) return baseGridResources;

    const sorted = sortResourcesWithPickedOnTop(baseGridResources, mediaPicked);
    gridOrderCacheRef.current = { baseKey, resources: sorted };
    return sorted;
  }, [baseGridResources, isEditingCarousel, isReplacingCarouselSlide, mediaPicked.length]);

  // Build a slide payload for the postImageCarousel node from a list of
  // resources. Used for both fresh inserts and edit-carousel updates.
  // We thread `resourceId` through so subsequent edits can match slides
  // back to their resources without depending on URL identity.
  const slidesFromResources = (list, previousSlides = []) => {
    const previousById = new Map();
    const previousBySrc = new Map();

    (Array.isArray(previousSlides) ? previousSlides : []).forEach((slide) => {
      if (!slide) return;
      const slideId = slide.resourceId != null ? String(slide.resourceId) : null;
      const slideSrc = typeof slide.src === 'string' ? slide.src : null;
      if (slideId && !previousById.has(slideId)) {
        previousById.set(slideId, slide);
      }
      if (slideSrc && !previousBySrc.has(slideSrc)) {
        previousBySrc.set(slideSrc, slide);
      }
    });

    return list
      .map((r) => {
        const resourceId = r.id != null ? String(r.id) : undefined;
        const slideSrcGuess =
          r?.downloadable_file_attachment_url || r?.display_image_file_url || null;
        const preservedSlide =
          (resourceId && previousById.get(resourceId)) ||
          (slideSrcGuess && previousBySrc.get(slideSrcGuess)) ||
          null;
        return carouselSlideFromResource(r, preservedSlide);
      })
      .filter(Boolean);
  };

  const confirmMediaPicks = () => {
    // Edit-carousel mode: always commit via onCarouselUpdate (≥2 enforced
    // by the disabled-state on the action button below). Reuses the same
    // slide-building helper so URL/resourceId metadata stays in sync with
    // a fresh insert.
    if (isEditingCarousel) {
      const slides = slidesFromResources(mediaPicked, existingCarouselSlides);
      if (slides.length < 2) {
        alert('A carousel needs at least two items. Add or remove media to get to 2+.');
        return;
      }
      if (onCarouselUpdate) onCarouselUpdate(slides);
      setMediaPicked([]);
      onClose();
      return;
    }

    if (!mediaPicked.length) return;
    if (mediaPicked.length === 1) {
      if (onSelect) onSelect(mediaPicked[0], 'image');
      setMediaPicked([]);
      onClose();
      return;
    }
    if (!onCarouselInsert) return;
    const slides = slidesFromResources(mediaPicked);
    if (slides.length < 2) {
      alert('Please select at least two usable assets.');
      return;
    }
    onCarouselInsert(slides);
    setMediaPicked([]);
    onClose();
  };

  const confirmCarouselPicks = () => {
    if (!onCarouselInsert || carouselPicked.length < 2) return;
    const slides = carouselPicked
      .map((r) => carouselSlideFromResource(r, null))
      .filter(Boolean);
    if (slides.length < 2) {
      alert('Some selected assets are not usable as images.');
      return;
    }
    onCarouselInsert(slides);
    setCarouselPicked([]);
    onClose();
  };

  // Add/remove body class to disable main menu interaction when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.classList.add('resource-modal-open');
      document.documentElement.classList.add('resource-modal-open');
      // Disable pointer events on main menu and site header
      const mainMenu = document.querySelector('#main-menu');
      const siteHeader = document.querySelector('.site-header');
      if (mainMenu) {
        mainMenu.style.pointerEvents = 'none';
        mainMenu.style.opacity = '0.3';
        mainMenu.style.zIndex = '1';
      }
      if (siteHeader) {
        siteHeader.style.pointerEvents = 'none';
        siteHeader.style.opacity = '0.3';
        siteHeader.style.zIndex = '1';
      }
    } else {
      document.body.classList.remove('resource-modal-open');
      document.documentElement.classList.remove('resource-modal-open');
      // Re-enable pointer events on main menu and site header
      const mainMenu = document.querySelector('#main-menu');
      const siteHeader = document.querySelector('.site-header');
      if (mainMenu) {
        mainMenu.style.pointerEvents = '';
        mainMenu.style.opacity = '';
        mainMenu.style.zIndex = '';
      }
      if (siteHeader) {
        siteHeader.style.pointerEvents = '';
        siteHeader.style.opacity = '';
        siteHeader.style.zIndex = '';
      }
    }
    
    // Cleanup on unmount
    return () => {
      document.body.classList.remove('resource-modal-open');
      document.documentElement.classList.remove('resource-modal-open');
      const mainMenu = document.querySelector('#main-menu');
      const siteHeader = document.querySelector('.site-header');
      if (mainMenu) {
        mainMenu.style.pointerEvents = '';
        mainMenu.style.opacity = '';
        mainMenu.style.zIndex = '';
      }
      if (siteHeader) {
        siteHeader.style.pointerEvents = '';
        siteHeader.style.opacity = '';
        siteHeader.style.zIndex = '';
      }
    };
  }, [isOpen]);

  if (!isOpen) return null;

  // Portal to <body> so the overlay escapes any transformed ancestor
  // (e.g. `.tiptap-editor-container.admin-tiptap-editor` uses
  // `transform: translateX(-50%)` to widen + center the editor, which
  // would otherwise become the containing block for `position: fixed`
  // and shift the modal off-screen).
  return createPortal(
    <div
      className="active_admin resource-modal-overlay"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div 
        className="resource-modal" 
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="resource-modal-header">
          <h2 className="resource-modal-title">
            {fileType === 'carousel'
              ? 'Build image carousel'
              : fileType === 'image'
                ? isEditingCarousel
                  ? 'Edit carousel'
                  : 'Select Image/Video'
                : fileType === 'video'
                  ? 'Select Video'
                  : fileType === 'link'
                    ? 'Select Link'
                    : 'Select File'}
          </h2>
          <button 
            onClick={onClose}
            className="resource-modal-close"
          >
            ×
          </button>
        </div>

        {/* Content - Left: Assets, Right: Carousel Order (top) + Upload (bottom) */}
        <div className="resource-modal-content">
          {/* Left Column - Existing Assets Grid */}
          <div className="resource-modal-left">
            <input
              type="text"
              placeholder="Search assets..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="resource-search-input"
            />

            {loading ? (
              <div style={{ textAlign: 'center', padding: '40px' }}>Loading assets...</div>
            ) : gridResources.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '40px' }}>
                No assets found. Upload a new file to get started.
              </div>
            ) : (
              <div className="resource-grid">
                {gridResources.map((resource) => (
                  <div
                    key={resource.id}
                    onClick={() => {
                      if (disabledResourceIds.has(String(resource.id))) return;
                      if (fileType === 'carousel') {
                        setCarouselPicked((prev) => {
                          const id = resource.id;
                          if (prev.find((r) => r.id === id)) {
                            return prev.filter((r) => r.id !== id);
                          }
                          return [...prev, resource];
                        });
                        return;
                      }
                      if (fileType === 'image') {
                        if (isReplacingCarouselSlide) {
                          setMediaPicked([resource]);
                          return;
                        }
                        setMediaPicked((prev) => {
                          const id = resource.id;
                          if (prev.find((r) => r.id === id)) {
                            return prev.filter((r) => r.id !== id);
                          }
                          return [...prev, resource];
                        });
                        return;
                      }
                      if (onSelect) onSelect(resource, fileType);
                      onClose();
                    }}
                    className={`resource-item${
                      fileType === 'carousel' && carouselPicked.some((r) => r.id === resource.id)
                        ? ' resource-item--carousel-picked'
                        : ''
                    }${
                      fileType === 'image' && mediaPicked.some((r) => r.id === resource.id)
                        ? ' resource-item--carousel-picked'
                        : ''
                    }${
                      disabledResourceIds.has(String(resource.id))
                        ? ' resource-item--resource-disabled'
                        : ''
                    }`}
                    aria-disabled={disabledResourceIds.has(String(resource.id))}
                  >
                    {disabledResourceIds.has(String(resource.id)) && (
                      <span className="resource-item-badge" aria-hidden="true">
                        Already in carousel
                      </span>
                    )}
                    {getResourcePreview(resource)}
                    <div className="resource-item-title">
                      {searchQuery ? highlightText(resource.title, searchQuery) : resource.title}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Right Column - Carousel Order (top) + Upload (bottom) */}
          <div className="resource-modal-right">
            {/* Top Section - Carousel Order */}
            {showCarouselOrderPanel && (
              <div className="resource-modal-right-top">
                <div className="resource-picked-order">
                  <div className="resource-picked-order__header">
                    <div>
                      <h3 className="resource-picked-order__title">Carousel order</h3>
                      <p className="resource-picked-order__copy">Use the arrows to rearrange the selected items before saving.</p>
                    </div>
                  </div>
                  <div className="resource-picked-order__list">
                    {mediaPicked.map((resource, index) => {
                      const label =
                        resource.title || resource.downloadable_file_filename || `${resource.file_type === 'VIDEO' ? 'Video' : 'Image'} ${index + 1}`;

                      return (
                        <div className="resource-picked-order__item" key={`${resource.id || label}-${index}`}>
                          <div className="resource-picked-order__thumb">
                            {getPickedMediaThumb(resource)}
                          </div>
                          <div className="resource-picked-order__meta">
                            <div className="resource-picked-order__position">{index + 1}</div>
                            <div className="resource-picked-order__label">{label}</div>
                            <div className="resource-picked-order__type">{resource.file_type === 'VIDEO' ? 'Video' : 'Image'}</div>
                          </div>
                          <div className="resource-picked-order__actions">
                            <button
                              type="button"
                              className="resource-picked-order__btn"
                              onClick={() => movePickedMedia(index, index - 1)}
                              disabled={index === 0}
                              aria-label={`Move ${label} earlier`}
                            >
                              ↑
                            </button>
                            <button
                              type="button"
                              className="resource-picked-order__btn"
                              onClick={() => movePickedMedia(index, index + 1)}
                              disabled={index === mediaPicked.length - 1}
                              aria-label={`Move ${label} later`}
                            >
                              ↓
                            </button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            )}

            {/* Bottom Section - Upload / Link Creation */}
            <div
              className={`resource-modal-right-bottom${showCarouselOrderPanel ? '' : ' resource-modal-right-bottom--full'}`}
            >
              <h3>
                {fileType === 'link'
                  ? 'Create New Link'
                  : `Upload New ${
                      fileType === 'carousel'
                        ? 'Images (multiple)'
                        : fileType === 'image'
                          ? 'Image/Video'
                          : fileType === 'video'
                            ? 'Video'
                            : 'File'
                    }`}
              </h3>
              
            {fileType === 'link' ? (
              // Link creation form
              <div className="resource-link-form">
                <div>
                  <label>
                    URL *
                  </label>
                  <input
                    type="url"
                    value={linkUrl}
                    onChange={(e) => setLinkUrl(e.target.value)}
                    placeholder="https://example.com"
                    className="resource-link-input"
                    disabled={creatingLink}
                  />
                </div>
                <div>
                  <label>
                    Title
                  </label>
                  <input
                    type="text"
                    value={linkTitle}
                    onChange={(e) => setLinkTitle(e.target.value)}
                    placeholder="Link title"
                    className="resource-link-input"
                    disabled={creatingLink}
                  />
                </div>
                <button
                  onClick={createLinkResource}
                  disabled={creatingLink || !linkUrl.trim()}
                  className="resource-link-button"
                >
                  {creatingLink ? 'Creating...' : 'Create Link'}
                </button>
              </div>
            ) : (
              // File upload area using paid extension UI style
              <div
                className={`resource-upload-area${uploadError ? ' has-upload-error' : ''}`}
                style={{ opacity: uploading ? 0.6 : 1, cursor: uploading ? 'not-allowed' : 'pointer' }}
                onClick={() => {
                  if (!uploading) fileInputRef.current?.click();
                }}
                onDragEnter={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  if (!uploading) {
                    e.currentTarget.style.borderColor = '#3b82f6';
                    e.currentTarget.style.backgroundColor = '#f0f7ff';
                  }
                }}
                onDragLeave={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  if (!uploading) {
                    e.currentTarget.style.borderColor = '#d1d5db';
                    e.currentTarget.style.backgroundColor = 'transparent';
                  }
                }}
                onDragOver={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                }}
                onDrop={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  if (!uploading && e.dataTransfer.files.length > 0) {
                    handleFileUpload(Array.from(e.dataTransfer.files));
                    // Reset styles after drop
                    e.currentTarget.style.borderColor = '#d1d5db';
                    e.currentTarget.style.backgroundColor = 'transparent';
                  }
                }}
              >
                <input
                  key={fileType || 'file'}
                  ref={fileInputRef}
                  type="file"
                  multiple={fileType === 'carousel' || fileType === 'image'}
                  accept={
                    fileType === 'carousel'
                      ? 'image/*,image/svg+xml'
                      : fileType === 'image'
                        ? 'image/*,image/svg+xml,video/*'
                        : fileType === 'video'
                          ? 'video/*'
                          : fileType === 'audio'
                            ? 'audio/*,.mp3,.m4a,.wav,.ogg,.aac,.flac,.weba,.wma'
                            : '*/*'
                  }
                  style={{ display: 'none' }}
                  onChange={handleFileSelect}
                  disabled={uploading}
                />
                
                {fileItems.length === 0 ? (
                  // Drag and drop area content (uses paid extension class for styling, but removes border since parent has it)
                  <div
                    className="tiptap-image-upload-drag-area"
                    style={{
                      flex: 1,
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      opacity: uploading ? 0.6 : 1,
                      pointerEvents: 'none',
                      // Override border and padding since parent resource-upload-area already has them
                      border: 'none',
                      borderRadius: '0',
                      padding: '0'
                    }}
                  >
                    <div style={{ fontSize: '48px', marginBottom: '15px' }}>📤</div>
                    <div style={{ fontSize: '16px', fontWeight: '500', marginBottom: '5px' }}>
                      <em>Click to upload</em>, drag and drop
                    </div>
                    <div style={{ fontSize: '12px', opacity: 0.7  }}>
                      {fileType === 'carousel' &&
                        'Images only — select at least 2 files at once • Multiple files'}
                      {fileType === 'image' && 'Images & Videos • Select 1 to insert, or 2+ to create carousel'}
                      {fileType === 'video' && 'Videos only • Maximum 1 file'}
                      {fileType === 'audio' && 'Audio files • Maximum 1 file'}
                      {fileType === 'download' && 'Any file type • Maximum 1 file'}
                    </div>
                    {(fileType === 'image' || fileType === 'carousel') && (
                      <div style={{ fontSize: '12px', opacity: 0.7, marginTop: '4px' }}>
                        {IMAGE_ASPECT_RATIO_HINT}
                      </div>
                    )}
                    {uploadError && (
                      <div className="tiptap-image-upload-error" role="alert" style={{ marginTop: '12px' }}>
                        {uploadError}
                      </div>
                    )}
                  </div>
                ) : (
                  // File preview (mimics ImageUploadPreview/FileUploadPreview)
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                    {fileItems.map((fileItem) => (
                      <div
                        key={fileItem.id}
                        className="tiptap-image-upload-preview"
                        style={{
                          border: '1px solid #e0e0e0',
                          borderRadius: '4px',
                          padding: '12px',
                          position: 'relative',
                          overflow: 'hidden'
                        }}
                      >
                        {fileItem.status === 'uploading' && (
                          <div
                            className="tiptap-image-upload-progress"
                            style={{
                              position: 'absolute',
                              top: 0,
                              left: 0,
                              height: '3px',
                              width: `${fileItem.progress}%`,
                              backgroundColor: '#007bff',
                              transition: 'width 0.3s'
                            }}
                          />
                        )}
                        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                          <div style={{ fontSize: '24px' }}>
                            {fileItem.file.type.startsWith('video/') ? '🎥' : '📄'}
                          </div>
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ fontSize: '14px', fontWeight: '500', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                              {fileItem.file.name}
                            </div>
                            <div style={{ fontSize: '12px' }}>
                              {fileItem.status === 'uploading' ? `Uploading... ${Math.round(fileItem.progress)}%` : `${(fileItem.file.size / 1024).toFixed(2)} KB`}
                            </div>
                          </div>
                          {fileItem.status === 'uploading' && (
                            <button
                              type="button"
                              onClick={() => {
                                setFileItems([]);
                                setUploading(false);
                                setUploadProgress(0);
                              }}
                              style={{
                                background: 'none',
                                border: 'none',
                                cursor: 'pointer',
                                padding: '4px',
                                fontSize: '18px'
                              }}
                            >
                              ×
                            </button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
            </div>
          </div>
        </div>

        {fileType === 'carousel' && (
          <div
            style={{
              padding: '16px 20px',
              borderTop: '1px solid #e5e7eb',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              gap: '12px',
              flexWrap: 'wrap',
            }}
          >
            <span style={{ fontSize: '14px', color: '#374151' }}>
              {carouselPicked.length} image(s) selected — pick at least two, then insert.
            </span>
            <button
              type="button"
              className="resource-link-button"
              disabled={carouselPicked.length < 2}
              onClick={confirmCarouselPicks}
            >
              Insert carousel
            </button>
          </div>
        )}

        {fileType === 'image' && (
          <div
            style={{
              padding: '16px 20px',
              borderTop: '1px solid #e5e7eb',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              gap: '12px',
              flexWrap: 'wrap',
            }}
          >
            <span style={{ fontSize: '14px', color: '#374151' }}>
              {isReplacingCarouselSlide
                ? `${mediaPicked.length} item(s) preselected — pick a replacement.`
                : isEditingCarousel
                ? `${mediaPicked.length} item(s) in carousel — pick at least 2.`
                : `${mediaPicked.length} selected — 1 inserts, 2+ becomes carousel.`}
            </span>
            <button
              type="button"
              className="resource-link-button"
              disabled={
                isReplacingCarouselSlide
                  ? mediaPicked.length < 1
                  : isEditingCarousel
                  ? mediaPicked.length < 2
                  : mediaPicked.length < 1
              }
              onClick={confirmMediaPicks}
            >
              {isReplacingCarouselSlide
                ? 'Replace slide'
                : isEditingCarousel
                ? 'Update carousel'
                : mediaPicked.length >= 2
                  ? 'Insert carousel'
                  : 'Insert media'}
            </button>
          </div>
        )}
      </div>
    </div>,
    document.body,
  );
};
